import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { $Button } from "us.common/components";
import { LeftOutlined, RightOutlined } from "us.icons";
import { Formik } from "formik";
import "./CaseNavigationBar.scss";
import _, { isArray } from "lodash";
import { RootState } from "us.helper/types";
import { MainSearchActions } from "us.common/actions";
import { ICaseNavigationBar } from "./Interface";
import { withRouter } from "us.common/wIthRouter";
import { MainCriterias } from "us.common/constants";
import { useNavigate } from "react-router-dom";

/**
 * @description - Case / Subcase search result previous and next case/subcase navigation
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3533013025/Case+Invoice+search+Next+Previous+Navigation
 * @author Roshan Maddumage <roshanma@unicorn-solutions.com>
 * @since 05/02/2024
 */
const CaseNavigationBar: React.FC<ICaseNavigationBar & PropsFromRedux> = (
  props
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    viewedIndex = 0,
    // history,
    viewedList,
    searchedList,
    searchedCriteria,
    updateViewedList,
  } = props;

  /**
   * @description - Handle case/invoice navigation to previous case/invoice or next case/invoice
   * @param {number} navigationKey - -1 for previous navigation and +1 for next navigation
   */
  const handleNavigation = (navigationKey: number) => {
    // get the next/previous view item index
    const newViewedIndex = viewedIndex + navigationKey;
    // get the next/previous view item exCaseNo
    const exCaseNo = searchedList[newViewedIndex].exCaseNo;

    // update the viewed case/invoice list and viewedIndex
    updateViewedList &&
      updateViewedList({
        viewedList: [...viewedList, exCaseNo],
        viewedIndex: newViewedIndex,
      });
    // navigate to the selected case
    navigate(`/case/${exCaseNo}`, { replace: true });
  };

  return (
    <Formik
      initialValues={{ search: "" }}
      onSubmit={() => {}}
      enableReinitialize
    >
      {({ values, ...restProps }: any) => (
        <>
          <div
            data-testid="case-navigation-bar"
            className="dom-search-result-nav d-flex align-items-center justify-content-center"
          >
            <$Button
              size="small"
              type="dashed"
              data-testid="case-navigation-bar-prev-btn"
              disabled={viewedIndex == 0}
              onClick={() => handleNavigation(-1)}
            >
              <LeftOutlined />
              {t("US.COLLECTION.LAYOUT:DOMAINVIEW.PREV_RESULT")}
            </$Button>
            <div className="dom-search-result-nav-text">
              <small className="d-block bui-label">
                {t(
                  `US.COLLECTION.LAYOUT:DOMAINVIEW.FILTERED_${
                    searchedCriteria === MainCriterias.CASE
                      ? "CASES"
                      : "INVOICES"
                  }`
                )}
              </small>
              <div>
                <strong data-testid="case-navigation-bar-current-index">
                  {viewedIndex + 1}
                </strong>
                <span className="mx-1">
                  {t("US.COLLECTION.LAYOUT:DOMAINVIEW.OF")}
                </span>
                <strong data-testid="case-navigation-bar-total-count">
                  {isArray(searchedList) ? searchedList.length : 0}
                </strong>
              </div>
            </div>
            <$Button
              size="small"
              type="dashed"
              data-testid="case-navigation-bar-next-btn"
              disabled={viewedIndex == searchedList.length - 1}
              onClick={() => handleNavigation(1)}
            >
              {t("US.COLLECTION.LAYOUT:DOMAINVIEW.NEXT_RESULT")}
              <RightOutlined />
            </$Button>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { mainSearch } = state;
  return {
    viewedIndex: mainSearch.viewedIndex,
    viewedList: mainSearch.viewedList,
    searchedList: mainSearch.searchedList,
    searchedCriteria: mainSearch.searchedCriteria,
  };
};

const { all } = MainSearchActions;

const mapDispatchToProps = {
  setNavigation: all.set,
  updateViewedList: all.updateViewedList,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CaseNavigationBar);
